import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    FormStore,
    inject,
    Input,
    Modal,
    ModalHandle,
    NothingHere,
    SelectInput,
    TasksStore,
    toArray,
} from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Form, FormGroup } from 'reactstrap';
import { ClientsStore } from '../Clients/ClientsStore';
import { AppStore } from '../Core/AppStore';
import { BsCardHeading } from '@react-icons/all-files/bs/BsCardHeading';
import { UsersStore } from '../Users/UsersStore';
import { EmailsStore } from '../Emails/EmailsStore';
import Select from 'react-select/src/Select';
import Editor from 'react-simple-wysiwyg';
import { WysiwygEditor } from 'Mri.Web.Common/Components/WysiwygEditor';

export interface IMassEmailsProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class MassEmails extends React.Component<IMassEmailsProps> {
    @action
    public async componentDidMount() {
        try {
            this.loading = true;

            await this.usersStore.loadUsersAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div className="col-xs-12 w-100">
                <div className="container">
                    <div className="row card px-4 py-2">
                        <div className="col-12">
                            <h3>Mass Emails</h3>
                        </div>
                        <div className="col-12">
                            <button className="btn btn-primary me-2" onClick={this.setDowntimeTemplate}>Downtime Template</button>
                            <button className="btn btn-primary me-2" onClick={this.setRestoredTemplate}>Restored Template</button>
                            <button className="btn btn-primary me-2" onClick={this.setUnexpectedTemplate}>Unexpected Template</button>
                        </div>
                        
                        <Form onSubmit={this.submitAsync} className="mt-3">
                            <div className="col-12">
                                <FormGroup>
                                    <label>Subject (SmartVu will be added to the beginning)</label>
                                    <Input
                                        type="text"
                                        field={this.massEmailModel.fields.subject}
                                        required
                                        className="d-inline-block"
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <label>PreHeader</label>
                                    <Input
                                        type="text"
                                        field={this.massEmailModel.fields.preHeader}
                                        required
                                        className="d-inline-block"
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <WysiwygEditor field={this.massEmailModel.fields.body} minHeight={400} required />
                                </FormGroup>{' '}
                            </div>
                            <div className="col-12">
                                <FormGroup>
                                    <label>Recipients</label> <br/>
                                    <SelectInput
                                        field={this.massEmailModel.fields.userEmails}
                                        options={this.userOptions}
                                        required
                                        isMulti
                                    />

                                    <button className="btn btn-link d-inline-block mt-2 px-0" onClick={this.selectAll}>Select All Users</button>
                                </FormGroup>

                            </div>
                            <FormGroup>
                                <button type="submit" className="btn btn-primary">
                                    Send Email
                                </button>
                            </FormGroup>
                        </Form>

                        <Modal
                            className="modal-sm"
                            title="Confirm Users"
                            modalHandle={this.modalHandle}
                            btnClass="w-50"
                            footerClass="d-flex justify-content-center"
                        >
                            <div className="text-center">
                                Are you user you want to send this email to these users?
                                {this.massEmailModel.fields.userEmails.value.map(e => <p>{e}</p>)}
                            </div>
                        </Modal>
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    private async selectAll(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        var userEmails = toArray(this.usersStore.users).map(u => u.email);
        this.massEmailModel.fields.userEmails.setValue(userEmails);
    }

    @bind
    @action
    private async submitAsync(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (await this.modalHandle.confirm()) {
            this.massEmailModel.onSubmit();
            if (this.massEmailModel.validate()) {
                const task = this.tasksStore.startSessionTask("Sending Email");

                try {
                    const secret = await this.emailsStore.sendDowntimeEmail(this.massEmailModel.values);
                    task.complete();

                    this.massEmailModel.clear();
                } catch (e: any) {
                    if (e.modelState) {
                        this.massEmailModel.clearGlobalErrors();
                        this.massEmailModel.addErrors(e.modelState);
                    }

                    task.error(e);
                }
            } else {
                console.log('invalid');
            }
        }
    }

    @bind
    @action
    private async setDowntimeTemplate(event: React.MouseEvent<HTMLButtonElement>) {
        this.massEmailModel.fields.subject.value = "Downtime Notification";
        this.massEmailModel.fields.preHeader.value = "SmartVu will be experiencing planned maintenance.";

        this.massEmailModel.fields.body.value = this.downtimeTemplate;
    }

    @bind
    @action
    private async setRestoredTemplate(event: React.MouseEvent<HTMLButtonElement>) {
        this.massEmailModel.fields.subject.value = "Restored"
        this.massEmailModel.fields.preHeader.value = "SmartVu is back to full working order!";

        this.massEmailModel.fields.body.value = this.restoredTemplate;
    }

    @bind
    @action
    private async setUnexpectedTemplate(event: React.MouseEvent<HTMLButtonElement>) {
        this.massEmailModel.fields.subject.value = "Unexpected Downtime"
        this.massEmailModel.fields.preHeader.value = "SmartVu is experiencing unexpected downtime.";
        this.massEmailModel.fields.body.value = this.unexpectedTemplate;
    }

    @computed
    private get userOptions() {
        var userEmails = toArray(this.usersStore.users).map(u => { return { label: u.email, value: u.email } });
       
        return userEmails;
    }

    private readonly massEmailModel = new FormStore<Mri.Web.Portal.Controllers.Emails.MassEmailModel>({
        subject: '',
        preHeader: '',
        body: '',
        userEmails: [],
    });

    @observable
    private secret?: string;
    @observable
    private loading: boolean = false;
    @observable
    private infoModalHandle = new ModalHandle();
    @observable
    private modalHandle = new ModalHandle();
    @inject
    private usersStore!: UsersStore;
    @inject
    private emailsStore!: EmailsStore;
    @inject
    private tasksStore!: TasksStore;
    @inject
    private appStore!: AppStore;
    private downtimeTemplate = `<div><br></div><div><b>Dear Valued Customer,</b></div><div><br></div><div>Please be advised.</div><div><span style="font-size: var(--bs-body-font-size); font-weight: var(--bs-body-font-weight); text-align: var(--bs-body-text-align); color: rgb(116, 120, 126); font-family: Arial, sans-serif;">SmartVu will be experiencing planned maintenance on </span><strong style="font-size: var(--bs-body-font-size); text-align: var(--bs-body-text-align); color: rgb(116, 120, 126); font-family: Arial, sans-serif;">Friday
October 22, 2024 from 2pm-4pm</strong><span style="font-size: var(--bs-body-font-size); font-weight: var(--bs-body-font-weight); text-align: var(--bs-body-text-align); color: rgb(116, 120, 126); font-family: Arial, sans-serif;">. These changes will help improve
the overall performance SmartVu.</span></div>

<p style="margin-top: 0in; line-height: 18pt;"><span style="font-family:&quot;Arial&quot;,sans-serif;
color:#74787E">We expect SmartVu to be down for about <strong>2 Hours</strong>. <u1:p style="font-family: Arial;"></u1:p></span><o:p></o:p></p>

<p style="margin-top: 0in; line-height: 18pt;"><span style="font-family:&quot;Arial&quot;,sans-serif;
color:#74787E">We appologize for any interruptions in service and will try to
have it back up as fast as possible. <u1:p style="font-family: Arial;"></u1:p></span><o:p></o:p></p>

<div><strong><span style="font-size:12.0pt;font-family:
&quot;Arial&quot;,sans-serif;mso-fareast-font-family:Aptos;mso-fareast-theme-font:minor-latin;
color:#74787E;mso-ansi-language:EN-US;mso-fareast-language:EN-US;mso-bidi-language:
AR-SA">For information or assistance contact the MRI Customer Service Team at: </span></strong><span style="font-size:12.0pt;font-family:&quot;Arial&quot;,sans-serif;mso-fareast-font-family:
Aptos;mso-fareast-theme-font:minor-latin;color:#74787E;mso-ansi-language:EN-US;
mso-fareast-language:EN-US;mso-bidi-language:AR-SA"><br>
<a href="mailto:CUSTOMERSERVICE@MRI-INC.NET"><span style="color:#348EDA">CUSTOMERSERVICE@MRI-INC.NET</span></a> (24 Hour Automated
Open Service Ticket) <br></div>`;
    private restoredTemplate = `<div><br></div><div><b>SmartVu has been restored.</b><br></div><div><b><br></b></div><div>We apologize again for the inconvenience and than you for your patience.</div><div><br></div><div><br></div><div><strong><span style="font-size:12.0pt;font-family:
&quot;Arial&quot;,sans-serif;mso-fareast-font-family:Aptos;mso-fareast-theme-font:minor-latin;
color:#74787E;mso-ansi-language:EN-US;mso-fareast-language:EN-US;mso-bidi-language:
AR-SA">For information or assistance contact the MRI Customer Service Team at: </span></strong><span style="font-size:12.0pt;font-family:&quot;Arial&quot;,sans-serif;mso-fareast-font-family:
Aptos;mso-fareast-theme-font:minor-latin;color:#74787E;mso-ansi-language:EN-US;
mso-fareast-language:EN-US;mso-bidi-language:AR-SA"><br>
<a href="mailto:CUSTOMERSERVICE@MRI-INC.NET"><span style="color:#348EDA">CUSTOMERSERVICE@MRI-INC.NET</span></a> (24 Hour Automated
Open Service Ticket)&nbsp;</span><br></div>`;
    private unexpectedTemplate = `<div><br></div><div><b>Dear Valued Customer,</b></div><div><br></div><div>Please be advised.</div><div><span style="font-size: var(--bs-body-font-size); font-weight: var(--bs-body-font-weight); text-align: var(--bs-body-text-align); color: rgb(116, 120, 126); font-family: Arial, sans-serif;">SmartVu is experiencing unexpected downtime. Our team is currently working&nbsp;</span><span style="text-align: var(--bs-body-text-align);"><font color="#74787e" face="Arial, sans-serif">diligently to have it back up as quickly as possible.</font></span></div><div><br></div>
<p style="margin-top: 0in; line-height: 18pt;"><span style="font-family:&quot;Arial&quot;,sans-serif;
color:#74787E">We apologize for any&nbsp;</span><span style="text-align: var(--bs-body-text-align);"><font color="#74787e" face="Arial, sans-serif">inconvenience this may cause.</font></span></p>
<div><strong><span style="font-size:12.0pt;font-family:
&quot;Arial&quot;,sans-serif;mso-fareast-font-family:Aptos;mso-fareast-theme-font:minor-latin;
color:#74787E;mso-ansi-language:EN-US;mso-fareast-language:EN-US;mso-bidi-language:
AR-SA">For information or assistance contact the MRI Customer Service Team at: </span></strong><span style="font-size:12.0pt;font-family:&quot;Arial&quot;,sans-serif;mso-fareast-font-family:
Aptos;mso-fareast-theme-font:minor-latin;color:#74787E;mso-ansi-language:EN-US;
mso-fareast-language:EN-US;mso-bidi-language:AR-SA"><br>
<a href="mailto:CUSTOMERSERVICE@MRI-INC.NET"><span style="color:#348EDA">CUSTOMERSERVICE@MRI-INC.NET</span></a> (24 Hour Automated
Open Service Ticket) <br></span></div>`;
}
